import { Project, CreateProjectDto, UpdateProjectDto, ProjectSearchParams } from '../types/project';
import { fetchApi } from '../lib/apiClient';

// 项目图片状态类型
export type ProjectImageStatus = 'not-started' | 'generating' | 'ready' | 'error';

// 图片状态检查结果
export interface ImageStatusResult {
  status: ProjectImageStatus;
  imageUrl?: string | null;
  error?: string;
}

export class ProjectService {
  static async getProjects(): Promise<Project[]> {
    return await fetchApi<Project[]>('/project/list', { method: 'GET' });
  }

  static async getProject(id: string): Promise<Project> {
    return await fetchApi<Project>(`/project/get?id=${id}`, { method: 'GET' });
  }

  static async createProject(projectData: CreateProjectDto): Promise<Project> {
    return await fetchApi<Project>('/project/create', {
      method: 'POST',
      body: projectData,
    });
  }

  static async updateProject(projectData: UpdateProjectDto): Promise<Project> {
    return await fetchApi<Project>('/project/update', {
      method: 'POST',
      body: projectData,
    });
  }

  static async deleteProject(id: string, cascadeDelete: boolean = false): Promise<void> {
    if (!id) {
      throw new Error('删除项目时必须提供 ID');
    }

    // 使用 fetchApi 发送删除请求，添加级联删除参数
    await fetchApi<void>(`/project/delete?id=${encodeURIComponent(id)}&cascadeDelete=${cascadeDelete}`, {
      method: 'DELETE',
    });

    console.log(`Project ${id} deleted successfully via service. Cascade delete: ${cascadeDelete}`);
  }

  static async searchProjects(params: ProjectSearchParams): Promise<Project[]> {
    return await fetchApi<Project[]>('/project/search', {
      method: 'POST',
      body: params,
    });
  }

  static async getOrCreateDefaultProject(): Promise<Project> {
    return await fetchApi<Project>('/project/default', { method: 'GET' });
  }

  static async mergeProjects(
    sourceProjectId: string,
    targetProjectId: string,
    keepSourceContent: boolean = false,
    newProjectTitle?: string
  ): Promise<Project> {
    if (!sourceProjectId || !targetProjectId) {
      throw new Error('合并项目时必须提供源项目ID和目标项目ID');
    }

    return await fetchApi<Project>('/project/merge', {
      method: 'POST',
      body: {
        sourceProjectId,
        targetProjectId,
        keepSourceContent,
        newProjectTitle
      },
    });
  }

  static async shareProject(projectId: string): Promise<Project> {
    if (!projectId) {
      throw new Error('分享项目时必须提供项目ID');
    }

    return await fetchApi<Project>('/project/share', {
      method: 'POST',
      body: { projectId },
    });
  }

  static async unshareProject(projectId: string): Promise<Project> {
    if (!projectId) {
      throw new Error('取消分享项目时必须提供项目ID');
    }

    return await fetchApi<Project>('/project/unshare', {
      method: 'POST',
      body: { projectId },
    });
  }

  static async getSharedProject(shareId: string): Promise<{project: Project, tasks: any[]}> {
    if (!shareId) {
      throw new Error('获取分享项目时必须提供分享ID');
    }

    return await fetchApi<{project: Project, tasks: any[]}>(`/project/s/${shareId}`, {
      method: 'GET',
    });
  }

  /**
   * 判断项目图片生成状态
   * @param project 项目对象
   * @returns 图片状态
   */
  static getImageStatus(project: Project): ProjectImageStatus {
    if (!project.isPublic || !project.shareId) {
      return 'not-started'; // 项目未分享
    }
    if (project.ogImageUrl) {
      return 'ready'; // 图片已生成
    }
    return 'generating'; // 正在生成中
  }

  /**
   * 轮询检查项目图片生成状态
   * @param projectId 项目ID
   * @param maxAttempts 最大轮询次数，默认30次
   * @param interval 轮询间隔（毫秒），默认2秒
   * @returns 图片状态结果
   */
  static async pollImageStatus(
    projectId: string,
    maxAttempts: number = 30,
    interval: number = 2000
  ): Promise<ImageStatusResult> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const project = await this.getProject(projectId);
        const status = this.getImageStatus(project);

        if (status === 'ready') {
          return {
            status: 'ready',
            imageUrl: project.ogImageUrl
          };
        }

        if (status === 'not-started') {
          return {
            status: 'error',
            error: '项目未分享或分享状态异常'
          };
        }

        // 如果是最后一次尝试，不再等待
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, interval));
        }
      } catch (error) {
        console.error('轮询图片状态失败:', error);
        return {
          status: 'error',
          error: error instanceof Error ? error.message : '检查图片状态失败'
        };
      }
    }

    return {
      status: 'error',
      error: '图片生成超时，请稍后重试'
    };
  }

  /**
   * 下载项目图片
   * @param project 项目对象
   * @param filename 文件名，可选
   */
  static async downloadProjectImage(project: Project, filename?: string): Promise<void> {
    if (!project.ogImageUrl) {
      throw new Error('项目图片尚未生成');
    }

    try {
      const response = await fetch(project.ogImageUrl);
      if (!response.ok) {
        throw new Error('下载图片失败');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `${project.title}-preview.png`;
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下载图片失败:', error);
      throw new Error('下载图片失败，请稍后重试');
    }
  }
}